import React, { useEffect, useState } from 'react'
import { ioGet, ioSend } from './api'
import { btnPrimary, btnSecondary, cardClass, inputClass } from './ui'

export default function SitesFacesAdmin() {
  const [sites, setSites] = useState([])
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [siteForm, setSiteForm] = useState({ venueUid: '', displayName: '', siteType: 'Indoor', venueType: '', area: '', description: '' })
  const [faceForm, setFaceForm] = useState({ duId: '', venueUid: '', faceNumber: '', description: '', mediaGroup: '', media: '' })

  const load = async () => {
    try {
      const data = await ioGet('/api/io/sites?includeRetired=1')
      setSites(data.sites || [])
    } catch (e) {
      setError(e.message)
    }
  }

  useEffect(() => { load() }, [])

  const saveSite = async (e) => {
    e.preventDefault()
    if (!siteForm.venueUid) return
    setBusy(true)
    setError('')
    try {
      await ioSend(`/api/io/sites/${encodeURIComponent(siteForm.venueUid)}`, 'PUT', siteForm)
      await load()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  const saveFace = async (e) => {
    e.preventDefault()
    if (!faceForm.duId) return
    setBusy(true)
    setError('')
    try {
      await ioSend(`/api/io/faces/${encodeURIComponent(faceForm.duId)}`, 'PUT', {
        venueUid: faceForm.venueUid,
        faceNumber: faceForm.faceNumber,
        description: faceForm.description,
        mediaGroup: faceForm.mediaGroup,
        media: faceForm.media,
      })
      await load()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  const retireFace = async (duId, retired) => {
    setBusy(true)
    try {
      await ioSend(`/api/io/faces/${encodeURIComponent(duId)}`, 'PUT', { retired, saleable: !retired })
      await load()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={`${cardClass} p-6 space-y-8`}>
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Static operations</h2>
        <p className="text-sm text-gray-500 mt-1">
          Overlay names, saleability, and retirement on Onboarding venues and Control display units.
          This does not create players.
        </p>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}

      <form onSubmit={saveSite} className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <h3 className="md:col-span-2 font-medium text-gray-900 dark:text-white">Edit site overlay</h3>
        <input className={inputClass} placeholder="Venue UID (site number)" value={siteForm.venueUid}
          onChange={(e) => setSiteForm({ ...siteForm, venueUid: e.target.value })} required />
        <input className={inputClass} placeholder="Display name" value={siteForm.displayName}
          onChange={(e) => setSiteForm({ ...siteForm, displayName: e.target.value })} />
        <select className={inputClass} value={siteForm.siteType}
          onChange={(e) => setSiteForm({ ...siteForm, siteType: e.target.value })}>
          <option>Indoor</option>
          <option>Outdoor</option>
          <option>Transit</option>
        </select>
        <input className={inputClass} placeholder="Venue type" value={siteForm.venueType}
          onChange={(e) => setSiteForm({ ...siteForm, venueType: e.target.value })} />
        <input className={inputClass} placeholder="Market / area" value={siteForm.area}
          onChange={(e) => setSiteForm({ ...siteForm, area: e.target.value })} />
        <input className={inputClass} placeholder="Description" value={siteForm.description}
          onChange={(e) => setSiteForm({ ...siteForm, description: e.target.value })} />
        <div className="md:col-span-2">
          <button type="submit" className={btnPrimary} disabled={busy}>Save site</button>
        </div>
      </form>

      <form onSubmit={saveFace} className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <h3 className="md:col-span-2 font-medium text-gray-900 dark:text-white">Link or edit a face</h3>
        <input className={inputClass} placeholder="Display unit ID" value={faceForm.duId}
          onChange={(e) => setFaceForm({ ...faceForm, duId: e.target.value })} required />
        <input className={inputClass} placeholder="Venue UID" value={faceForm.venueUid}
          onChange={(e) => setFaceForm({ ...faceForm, venueUid: e.target.value })} />
        <input className={inputClass} placeholder="Face number" value={faceForm.faceNumber}
          onChange={(e) => setFaceForm({ ...faceForm, faceNumber: e.target.value })} />
        <input className={inputClass} placeholder="Description" value={faceForm.description}
          onChange={(e) => setFaceForm({ ...faceForm, description: e.target.value })} />
        <input className={inputClass} placeholder="Media group" value={faceForm.mediaGroup}
          onChange={(e) => setFaceForm({ ...faceForm, mediaGroup: e.target.value })} />
        <input className={inputClass} placeholder="Media format" value={faceForm.media}
          onChange={(e) => setFaceForm({ ...faceForm, media: e.target.value })} />
        <div className="md:col-span-2">
          <button type="submit" className={btnPrimary} disabled={busy}>Save face</button>
        </div>
      </form>

      <div>
        <h3 className="font-medium text-gray-900 dark:text-white mb-3">Current hierarchy</h3>
        <div className="space-y-4">
          {sites.map((site) => (
            <div key={site.siteId} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
              <div className="flex justify-between gap-2">
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">{site.name}</div>
                  <div className="text-xs text-gray-500">{site.venueUid || site.siteId}</div>
                </div>
                {site.venueUid && (
                  <button
                    type="button"
                    className={btnSecondary}
                    onClick={() => setSiteForm({
                      venueUid: site.venueUid,
                      displayName: site.name || '',
                      siteType: site.siteType || 'Indoor',
                      venueType: site.venueType || '',
                      area: site.area || '',
                      description: site.description || '',
                    })}
                  >
                    Load
                  </button>
                )}
              </div>
              <ul className="mt-2 space-y-1">
                {(site.faces || []).map((f) => (
                  <li key={f.id} className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
                    <span>Face {f.faceNumber || f.id} — {f.name}</span>
                    <span className="space-x-2">
                      <button type="button" className="text-blue-600" onClick={() => setFaceForm({
                        duId: String(f.id),
                        venueUid: site.venueUid || '',
                        faceNumber: f.faceNumber || '',
                        description: f.faceDescription || '',
                        mediaGroup: f.mediaGroup || '',
                        media: f.media || '',
                      })}>Edit</button>
                      <button type="button" className="text-red-600" onClick={() => retireFace(f.id, !f.retired)}>
                        {f.retired ? 'Unretire' : 'Retire'}
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
